import { ContentLoading } from '@/components/common/content-loading';
import {
    AllFloorsGuide,
    ChangeRegionButton,
    FloorGuideVerticalPoiModal,
    MinimalPoiModal,
    NavigationGuide,
    NearbyPoiListDropdown,
    NoCategoryPoiPopup,
    PoiListPopup,
    SelectTransportationModeModal,
    VerticalCategoryList,
    VerticalFloorList,
    VerticalMapHeader,
    ZoomControlBtnGroup
} from '@/components/feature/floor-guide';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@/components/icons';
import { JAMSIL_MAP_IDS } from '@/constants/app';
import { FOCUS_TO_OBJECT_PADDING, FOCUS_TO_OPTIONS, MAP_CONTRAST_COLOR, OBJECT_GROUP, POI_ID_DELIMITER, ZOOM_SCROLL_CONTAINER_SELECTOR } from '@/constants/floor-guide';
import { zoomLevelToScale } from '@/constants/zoom';
import type { FloorInfo } from '@/services/dto';
import { FloorGuideType, NavigationStatus } from '@/enums/floor-guide';
import { useAccessibilityTTS, useRestoreFocusOnClose } from '@/hooks';
import { useCalculateIconSize, useFloorGuideState, useMagnifyMapScrollThrough, usePoiContentModalState, usePoiContentState } from '@/hooks/floor-guide';
import {
    useBuildingCategory,
    useFloor,
    useFloorCategory,
    useMapControl,
    useMarker,
    useMyCurrentLocation,
    usePoi,
    useRouteDirection,
    useRouteSimulation
} from '@/hooks/map';
import { useMap } from '@/hooks/map/useMap';
import { useMapEvents } from '@/hooks/map/useMapEvents';
import { useMapMove } from '@/hooks/map/useMapMove';
import { useMapZoomLevel } from '@/hooks/map/useMapZoomLevel';
import { useCurrentBuilding } from '@/hooks/useCurrentBuilding';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import { useInteraction } from '@/hooks/useInteraction';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useModalStackStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { useMarkerStore } from '@/stores/markerStore';
import type { NearbyPoiItemType } from '@/types/map';
import {
    generateFloorName,
    getFloorMapName,
    getNearestAvailablePoiItem,
    getPoiItemByTitle,
    isNonEmptyObject,
    resetObjectStyle,
    applyMapLanguageToMap,
    resolveMapFloorName,
    updateObjectGroup,
    updateObjectStyle,
} from '@/utils/floor-guide';
import { findNearestFloorWithPoi, generateQrHereUrl, getPoiObjectIds, safeMoveToMarker, waitForMarkerReady } from '@/utils/map';
import { isMinimalPoiSlide } from '@/utils/poi/isMinimalPoiSlide';
import {
    addDistanceToPoiList,
    calculateEndPointFromPoi,
    calculateGetCurrentPoiOptions,
    getCurrentPois,
    getPoiById,
    resolveBuildingNameForNearbyPoiItem,
    sortNearbyPoiListByDistance,
    waitForPoisToLoad,
} from '@/utils/map-poi';
import { mapPoiListToMarker, mapPoiToMarker } from '@/utils/marker';
import { isValidNumber } from '@/utils/number';
import { buildNavigationParams, trackFloorGuideNavigation } from '@/utils/navigation-tracking';
import { generateDisplayDirections, generateGetRouteOptions } from '@/utils/route-simulation';
import clsx from 'clsx';
import { FOCUS_TYPE, type TRANSPORTATION_REQUEST_TYPE } from 'dabeeomaps';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import type { INaviAnimOption } from 'dabeeomaps/dist/src/model/map/INaviAnimOption';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export default function VerticalView() {
    const { i18n, t } = useTranslation();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const didMountRef = useRef(false);
    const processedPoiRef = useRef<string | null>(null);
    const processedParkingRef = useRef<string | null>(null);
    const pendingMarkerIdsRef = useRef<string[]>([]);
    const lastMarkerTimestampRef = useRef<number>(0);
    const {
        state,
        modalState,
        handleSetModalState,
        handleSetState,
        handleSetNearbyState,
        nearbyState,
        handleResetNearbyState,
        handleGetPoiContentById,
        handleGetBuildingNameById,
    } = useFloorGuideState();
    const { selectedPoi, floorGuideType, resetSelectedPoi, resetFloorGuideType, selectedParkingSlot, resetSelectedParkingSlot } = useFloorGuideStore();
    const { speak } = useAccessibilityTTS();
    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const language = useConfigStore((state) => state.config.ui.language);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const zoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const zoomLevel = useConfigStore((state) => state.config.ui.zoomLevel);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId) || '';
    const isJamsil = JAMSIL_MAP_IDS.includes(mapId);

    const barrierFreeScale = zoomLevelToScale(zoomLevel);
    // Layout kiosk đã bị scale bởi MainLayout, nên map được scale nghịch lại để canvas render ở kích thước gốc,
    // giúp toạ độ hit-test POI khớp với điểm người dùng chạm.
    const mapBarrierFreeStyle = useMemo((): CSSProperties => {
        if (zoomClosed || barrierFreeScale <= 1) {
            return {
                pointerEvents: 'auto',
                touchAction: 'auto',
            };
        }

        return {
            pointerEvents: 'none',
            transform: `scale(${1 / barrierFreeScale})`,
            transformOrigin: 'top left',
            width: `${barrierFreeScale * 100}%`,
            height: `${barrierFreeScale * 100}%`,
        };
    }, [zoomClosed, barrierFreeScale]);

    const { markerIds, setMarkerIds } = useMarkerStore();
    const { resetCountdown } = useGlobalCountdownReset();
    const { restoreFocus } = useRestoreFocusOnClose({ active: modalState.noPoiCategory });

    const { initMap, cleanupMap, mapData, currentMap, isMapReady, loading } = useMap({
        clientId: server?.store?.mapClientId ?? '',
        clientSecret: server?.store?.mapSecret ?? '',
        studioVersion: 4,
    });
    const cleanupMapRef = useRef(cleanupMap);

    // Update the ref whenever cleanupMap changes
    cleanupMapRef.current = cleanupMap;

    const {
        modalState: poiDetailModalState,
        handleSetModalState: handleSetPoiDetailModalState,
        startPoint,
        endPoint,
        setEndPointFromPoi,
        navigationOptions,
        walkingOptions,
        routes,
        selectedRoute,
        selectedEndPointId,
        poiItemBySelectedEndPointId,
        generateNavigationGuideEndPoint,
        currentPoiOptions,
        destinationIconUrl,
        isDestinationIconReady,
        setRoutes,
        setSelectedRoute,
        handlePoiClick,
        clickedMapPoiName,
        handleResetModalState,
        selectedContentId,
        setSelectedContentId,
    } = usePoiContentState(mapData, currentMap);

    const { addMyCurrentLocation } = useMyCurrentLocation(currentMap);

    const { calculateIconSize } = useCalculateIconSize();
    const {
        selectedFloorName,
        handleChangeFloorByName,
        selectedFloor: selectedFloorItem,
        getFloorById,
        setSelectedFLoorName,
        getFloorsByBuildingId,
        handleChangeFloor,
        handleSelectStoreFloor,
    } = useFloor(currentMap, mapData);
    const [sortedFloors, setSortedFloors] = useState<FloorInfo[]>([]);
    const { handleZoomIn, handleZoomOut, isDisabledZoomIn, isDisabledZoomOut, setZoomLevel } = useMapZoomLevel(currentMap);
    const { selectedCategory, setSelectedCategoryCode, groupedCategoryByClientCode } = useBuildingCategory();
    const { addMarker, clearMarker } = useMarker(currentMap);
    const { groupedPoiByFloorId } = usePoi(currentMap, mapData);
    const { currentBuilding } = useCurrentBuilding();
    const { poiContentSlide, relatedPoiContentList, selectedPoiContentId, setSelectedPoiContent } = usePoiContentModalState(
        selectedEndPointId,
        selectedFloorItem,
        clickedMapPoiName,
        selectedContentId
    );
    const isMinimalPoiDetail = isMinimalPoiSlide(poiContentSlide, language);
    const { setPoi, resetPoi } = usePoi(currentMap);
    const { getRouteDirection } = useRouteDirection(mapData);
    const { drawRouteSimulation, startRouteSimulation, pauseRouteSimulation, resumeRouteSimulation, stopRouteSimulation, clearRouteSimulation } =
        useRouteSimulation(currentMap);
    const { handleFocusTo, handleMoveTo } = useMapControl(currentMap);
    const { moveUp, moveDown, moveLeft, moveRight } = useMapMove(currentMap);

    const allPoiItems = useMemo(() => {
        if (!buildings) {
            return [];
        }
        return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
    }, [buildings]);

    const allStoreFloors = useMemo(() => {
        if (!buildings?.length) {
            return [];
        }
        return buildings.flatMap((building) =>
            (building.floors ?? []).map((floor) => ({
                ...floor,
                buildingId: floor.buildingId || building.studioBuildingId,
            }))
        );
    }, [buildings]);

    const [currentMapBuildingId, setCurrentMapBuildingId] = useState<string | undefined>(currentBuilding?.studioBuildingId || undefined);

    const { selectedFloor } = useFloorCategory(selectedFloorName, currentMapBuildingId);

    useEffect(() => {
        setSortedFloors(getFloorsByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || ''));
    }, [currentMapBuildingId, getFloorsByBuildingId]);

    useEffect(() => {
        if (!selectedPoi) return;
        setCurrentMapBuildingId(selectedPoi?.buildingId);
    }, [selectedPoi, mapData?.dataBuilding]);

    const getPoiItemByStudioPoiId = useCallback(
        (studioPoiId: string) => {
            if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                return undefined;
            }

            return allPoiItems.find((item) => item?.studioPoiId === studioPoiId);
        },
        [allPoiItems]
    );

    const handleStopSimulation = async () => {
        stopRouteSimulation();
        clearRouteSimulation();
        handleSetModalState({ navigationGuide: false });
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();
        resetSelectedParkingSlot();
        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }
        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);

        await addMyCurrentLocation();
    };

    const handleSelectCategory = async (buildingCategory: any) => {
        // Create unique timestamp for this call
        const currentTimestamp = Date.now();
        lastMarkerTimestampRef.current = currentTimestamp;

        setSortedFloors(getFloorsByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || ''));

        // clear route simulation
        handleSetModalState({ nearbyPoiList: false, navigationGuide: false });
        handleResetNearbyState();
        stopRouteSimulation();
        clearRouteSimulation();
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();

        // Clear pending markers from previous calls that haven't completed yet
        if (Array.isArray(pendingMarkerIdsRef.current) && pendingMarkerIdsRef.current?.length > 0) {
            clearMarker(pendingMarkerIdsRef.current);
            pendingMarkerIdsRef.current = [];
        }

        // Clear existing markers
        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
        }

        if (markerIds?.selectedPoi) {
            clearMarker(markerIds?.selectedPoi);
        }

        // Reset marker state immediately
        setMarkerIds({ poiList: undefined, selectedPoi: undefined });

        // Small delay to ensure markers are cleared before adding new ones
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!buildingCategory?.clientCode) {
            return;
        }

        let categoriesCode: string[] = [];

        if (!isJamsil) {
            categoriesCode = groupedCategoryByClientCode[buildingCategory.clientCode] ?? buildingCategory.mergeCategoryCode ?? [buildingCategory.categoryCode] ;
        } else {
            categoriesCode = [buildingCategory.categoryCode];
        }

        const poiList = categoriesCode.flatMap((item) => {
            const poi = groupedPoiByFloorId.get(selectedFloorName)?.get(item);
            return poi ? poi : [];
        });

        if (!poiList?.length || poiList?.length === 0) {
            const nearestFloor = findNearestFloorWithPoi(sortedFloors, selectedFloorName, groupedPoiByFloorId, categoriesCode, allStoreFloors);
            handleSetState({ nearestPoiFloor: nearestFloor });
            setMarkerIds({ poiList: undefined, selectedPoi: undefined });
            if (nearestFloor) {
                handleSetModalState({ noPoiCategory: true });
            }
        }

        // NEARBY
        if (Array.isArray(poiList) && poiList?.length) {
            const nearbyPoiList: NearbyPoiItemType[] = [];
            for (const poi of poiList) {
                const closestPoiOptions: IClosestPoiDataOption = {
                    floorId: poi?.floorId ?? '',
                    position: {
                        x: poi?.position?.x ?? 0,
                        y: poi?.position?.y ?? 0,
                    },
                };

                const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);

                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }

            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);

            if (sortedNearbyPoiList?.length) {
                const nearestPoi = sortedNearbyPoiList[0];
                const fallbackBuildingName =
                    handleGetBuildingNameById(currentMapBuildingId || currentBuilding?.studioBuildingId || '') ?? '';
                const buildingName = resolveBuildingNameForNearbyPoiItem(nearestPoi, buildings, language, fallbackBuildingName);
                const selectedPoiId = `${nearestPoi?.id}${POI_ID_DELIMITER}${nearestPoi?.nearbyPoi?.id}`;
                handleSetNearbyState({
                    nearbyPoiList: sortedNearbyPoiList,
                    selectedPoiId: selectedPoiId,
                    title: buildingCategory?.name || '',
                    floorName: selectedFloorName ?? '',
                    building: buildingName,
                    type: 'category',
                });
                handleSetModalState({ nearbyPoiList: true });
            }
        }

        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
        const newMarkerIds = await addMarker({ marker: markers });

        // Check again if this is still the latest call after async operation
        if (currentTimestamp !== lastMarkerTimestampRef.current) {
            // Newer call has been made, clean up markers we just added
            if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
                clearMarker(newMarkerIds);
            }
            return;
        }

        // Store marker IDs in ref for cleanup in next call
        if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
            pendingMarkerIdsRef.current = newMarkerIds;
        }

        if (markers?.length) {
            speak(t('floor_guide.text_to_speech.click.location_this_floor', { number: markers?.length }));
        }

        setMarkerIds({ poiList: newMarkerIds });
        setSelectedCategoryCode(buildingCategory?.categoryCode);

        const objectIds = getPoiObjectIds(poiList);

        if (!objectIds?.length) {
            return;
        }

        if (objectIds.length > 1) {
            handleFocusTo({
                transition: true,
                focus: {
                    type: FOCUS_TYPE.OBJECT,
                    ids: objectIds,
                },
                padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
            });
            return;
        }

        handleMoveTo({
            position: {
                x: poiList[0]?.position?.x ?? 0,
                y: poiList[0]?.position?.y ?? 0,
            },
            floorId: poiList[0]?.floorId,
            transition: true,
        });
    };

    const handleGoToFloor = async () => {
        if (!state.nearestPoiFloor) {
            return;
        }

        const floorMapName = getFloorMapName(state.nearestPoiFloor);
        const selectedFloorName = state.nearestPoiFloor.name[i18n.language || 'ko'] || floorMapName;

        if (!floorMapName && !selectedFloorName) {
            return;
        }

        const targetBuildingId = state.nearestPoiFloor.buildingId;
        const activeBuildingId = currentMapBuildingId || currentBuilding?.studioBuildingId || '';
        const isCrossBuilding = Boolean(targetBuildingId && targetBuildingId !== activeBuildingId);

        if (isCrossBuilding) {
            setCurrentMapBuildingId(targetBuildingId);
            await handleChangeFloor(state.nearestPoiFloor.floor_code);
        } else if (!isJamsil) {
            await handleChangeFloorByName(floorMapName);
        } else {
            await handleChangeFloor(state.nearestPoiFloor.floor_code);
        }

        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
            setMarkerIds({ poiList: undefined });
        }

        if (!selectedCategory) {
            return;
        }

        let categoriesCode: string[] = [];

        if (!isJamsil) {
            categoriesCode = groupedCategoryByClientCode[selectedCategory.clientCode] ?? selectedCategory.mergeCategoryCode ?? [selectedCategory.categoryCode];
        } else {
            categoriesCode = [selectedCategory.categoryCode];
        }

        const poiFloorKey = !isJamsil ? floorMapName : selectedFloorName;

        const poiList = categoriesCode.flatMap((item) => {
                const poi = groupedPoiByFloorId.get(poiFloorKey)?.get(item);
                return poi ? poi : [];
        });

        if (!Array.isArray(poiList) || !poiList?.length) {
            return;
        }

        // NEARBY
        const nearbyPoiList: NearbyPoiItemType[] = [];
        for (const poi of poiList) {
            const closestPoiOptions: IClosestPoiDataOption = {
                floorId: poi?.floorId ?? '',
                position: {
                    x: poi?.position?.x ?? 0,
                    y: poi?.position?.y ?? 0,
                },
            };

            const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);

            if (Array.isArray(orderedPoiList) && orderedPoiList?.length > 0) {
                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }
        }

        if (nearbyPoiList?.length) {
            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);

            const buildingName = handleGetBuildingNameById(targetBuildingId || currentMapBuildingId || currentBuilding?.studioBuildingId || '');

            if (sortedNearbyPoiList?.length) {
                const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                handleSetNearbyState({
                    nearbyPoiList: sortedNearbyPoiList,
                    selectedPoiId: selectedPoiId,
                    title: selectedCategory?.name || '',
                    floorName: selectedFloorName ?? '',
                    building: buildingName ?? '',
                    type: 'category',
                });
                handleSetModalState({ nearbyPoiList: true });
            }
        }

        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
        const newMarkerIds = await addMarker({ marker: markers });

        setMarkerIds({ poiList: newMarkerIds });
        handleSetModalState({ noPoiCategory: false });

        const objectIds = getPoiObjectIds(poiList);

        if (!objectIds?.length) {
            return;
        }

        if (objectIds.length > 1) {
            setTimeout(() => {
                handleFocusTo({
                    transition: true,
                    focus: {
                        type: FOCUS_TYPE.OBJECT,
                        ids: objectIds,
                    },
                    padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
                });
            }, 100);
            return;
        }

        setTimeout(() => {
            handleMoveTo({
                position: {
                    x: poiList[0]?.position?.x ?? 0,
                    y: poiList[0]?.position?.y ?? 0,
                },
                floorId: poiList[0]?.floorId,
                transition: true,
            });
        }, 100);
    };

    const handleStart = useCallback(
        (animationOptions?: INaviAnimOption) => {
            startRouteSimulation(animationOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });
        },
        [startRouteSimulation, handleSetState]
    );

    const handleCloseNearbyPoiList = async () => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();

        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }
        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);
        await addMyCurrentLocation();
    };

    const handleStartNavigationGuide = async (poi: IPoi) => {
        // Set endpoint - no timing issues since it's direct state!
        setEndPointFromPoi(poi);

        // Calculate endpoint for immediate use
        const calculatedEndPoint = calculateEndPointFromPoi(poi, mapData);

        // Use immediately
        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, calculatedEndPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMarker(markerIds.myLocation ?? '');
        setMarkerIds({ myLocation: undefined });

        setRoutes(routeDirections);

        if (startPoint?.floorName !== calculatedEndPoint.floorName) {
            handleSetModalState({ selectTransportationMode: true });
            handleResetModalState();
            return;
        }

        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        trackFloorGuideNavigation(
            buildNavigationParams({
                startBuildingId: server?.building?.studioBuildingId ?? '',
                startPoint,
                endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                endPoint: calculatedEndPoint,
                endPoiId: poi?.id ?? '',
            })
        );
        handleResetModalState();
        handleSetModalState({ navigationGuide: true });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleStartGuide = async () => {
        if (!endPoint) {
            return;
        }
        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, endPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMarker(markerIds.myLocation ?? '');
        setMarkerIds({ myLocation: undefined });

        setRoutes(routeDirections);
        if (startPoint?.floorName !== endPoint?.floorName) {
            handleSetModalState({ selectTransportationMode: true, poiList: false });
            handleResetModalState();
            return;
        }
        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        trackFloorGuideNavigation(
            buildNavigationParams({
                startBuildingId: server?.building?.studioBuildingId ?? '',
                startPoint,
                endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                endPoint,
                endPoiId: selectedEndPointId ?? endPoint.poiId ?? '',
            })
        );
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, poiList: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleSelectTransportationMode = async (mode: TRANSPORTATION_REQUEST_TYPE) => {
        if (!routes || !Object.keys(routes)?.length || !routes[mode]) {
            return;
        }
        if (endPoint) {
            trackFloorGuideNavigation(
                buildNavigationParams({
                    startBuildingId: server?.building?.studioBuildingId ?? '',
                    startPoint,
                    endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                    endPoint,
                    endPoiId: selectedEndPointId ?? endPoint.poiId ?? '',
                    transportMode: mode,
                })
            );
        }
        setSelectedRoute(routes[mode]);
        await drawRouteSimulation(routes[mode], navigationOptions);
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, selectTransportationMode: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleResumeSimulation = () => {
        resumeRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handlePauseSimulation = () => {
        pauseRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.PAUSED });
    };

    const handleWatchAgain = () => {
        startRouteSimulation(walkingOptions);
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handleShowMyLocation = async (type?: 'show-location') => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();
        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);

        if (type === 'show-location') {
            const currentPoiList = getCurrentPois(mapData, currentPoiOptions);
            if (Array.isArray(currentPoiList) && currentPoiList?.length && currentPoiList[0]?.id) {
                const poiItem = getPoiItemByStudioPoiId(currentPoiList[0]?.id);
                if (poiItem) {
                    speak(
                        `${t('floor_guide.text_to_speech.current_location', { floorName: generateFloorName(startPoint?.floorName ?? ''), poiName: poiItem?.name?.[language] || poiItem?.name?.['ko'] || '' })}`
                    );
                }
            }
        }
        await addMyCurrentLocation();
    };

    useMapEvents({
        mapContainer: mapContainerRef,
        onPoiClick: async (data) => {
            stopRouteSimulation();
            clearRouteSimulation();

            handleSetModalState({ navigationGuide: false, nearbyPoiList: false });

            setRoutes(undefined);
            handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
            resetSelectedPoi();
            resetFloorGuideType();
            handlePoiClick(data);
            handleResetNearbyState();
        },
        onNavigationComplete: async () => {
            handleSetState({ navigationStatus: NavigationStatus.DONE });
            setRoutes(undefined);
            setTimeout(() => {
                handleFocusTo(FOCUS_TO_OPTIONS);
            }, 100);
            if (endPoint?.floorName) {
                await handleChangeFloorByName(endPoint.floorName);
            }
        },
        onZoomChanged: (data) => {
            setZoomLevel(data?.changedValue ?? 1);
        },
        onFloorChanged: (data) => {
            applyMapLanguageToMap(currentMap, language);

            if (Array.isArray(data?.name) && data?.name?.length) {
                setSelectedFLoorName(resolveMapFloorName(data.name));
            }
            if (contrast) {
                updateObjectGroup(mapData, OBJECT_GROUP);
                updateObjectStyle(currentMap, OBJECT_GROUP);
                setPoi(MAP_CONTRAST_COLOR.POI);
            } else {
                resetObjectStyle(currentMap);
                resetPoi();
            }

            applyMapLanguageToMap(currentMap, language);
        },
    });

    useEffect(() => {
        initMap(mapContainerRef);
    }, [initMap]);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (!mapContainerRef.current) {
            return;
        }

        cleanupMap();

        // Single RAF is sufficient - ensures DOM is ready after cleanup
        const rafId = requestAnimationFrame(() => {
            initMap(mapContainerRef);
        });

        return () => {
            cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenMode]);

    useEffect(() => {
        return () => {
            cleanupMapRef.current();
        };
    }, []); // Empty dependency array - only run on mount/unmount

    useEffect(() => {
        if (
            !selectedParkingSlot?.studioFloorID ||
            !selectedParkingSlot?.parkingSlotTitle ||
            floorGuideType !== FloorGuideType.DIRECTION_FROM_PARKING ||
            !mapData ||
            !isMapReady
        ) {
            return;
        }

        const run = async () => {
            const currentParkingKey = `${selectedParkingSlot.studioFloorID}-${selectedParkingSlot.parkingSlotTitle}-${floorGuideType}`;
            if (processedParkingRef.current === currentParkingKey) {
                return;
            }

            processedParkingRef.current = currentParkingKey;

            let poiItem: IPoi | IPoi[] | undefined;

            poiItem = mapData?.dataPoi?.find({
                title: selectedParkingSlot?.parkingSlotTitle,
                floorId: selectedParkingSlot?.studioFloorID,
            });

            if (!poiItem) {
                poiItem = mapData?.dataPoi?.find({
                    title: selectedParkingSlot?.parkingSlotTitle,
                });
            }

            const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === selectedPoi?.studioFloorID);

            const filteredPoiItem = getPoiItemByTitle(poiItem, selectedParkingSlot?.parkingSlotTitle);
            setEndPointFromPoi(filteredPoiItem);

            if (!filteredPoiItem) {
                processedParkingRef.current = null;
                return;
            }

            const destinationPoint = {
                floorId: filteredPoiItem?.floorId ?? '',
                floorName: resolveMapFloorName(floor?.name) ?? '',
                position: {
                    x: filteredPoiItem?.position?.x ?? 0,
                    y: filteredPoiItem?.position?.y ?? 0,
                },
                poiId: filteredPoiItem?.id,
            };

            const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, destinationPoint));
            if (!routeDirections || !Object.keys(routeDirections)?.length) {
                processedParkingRef.current = null;
                return;
            }

            clearMarker(markerIds.myLocation ?? '');
            setMarkerIds({ myLocation: undefined });
            setRoutes(routeDirections);
            if (startPoint?.floorName !== destinationPoint.floorName) {
                handleSetModalState({ selectTransportationMode: true });
                handleResetModalState();
                // Reset state
                resetSelectedPoi();
                resetFloorGuideType();
                resetSelectedParkingSlot();
                processedParkingRef.current = null;
                return;
            }

            setSelectedRoute(routeDirections?.recommendation);
            await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
            handleResetModalState();
            handleSetModalState({ navigationGuide: true });
            handleStart(walkingOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });

            // reset state
            resetSelectedParkingSlot();
            resetFloorGuideType();
            processedParkingRef.current = null;
        };
        run();
    }, [
        currentMap,
        selectedParkingSlot,
        floorGuideType,
        isMapReady,
        startPoint,
        mapData?.dataPoi,
        mapData?.dataFloor,
        navigationOptions,
        walkingOptions,
        markerIds.myLocation,
        calculateIconSize,
        handleChangeFloorByName,
        getRouteDirection,
        handleSetModalState,
        handleResetModalState,
        setRoutes,
        resetSelectedPoi,
        resetSelectedParkingSlot,
        resetFloorGuideType,
        addMarker,
        handleStart,
        drawRouteSimulation,
        setSelectedRoute,
        clearMarker,
        setMarkerIds,
        handleSetState,
        setEndPointFromPoi,
        handleSetNearbyState,
        currentMap?.pois?.getCurrentPois,
        handleGetPoiContentById,
        language,
        selectedFloorName,
        handleGetBuildingNameById,
        mapData,
        selectedPoi?.studioFloorID,
    ]);

    useEffect(() => {
        const run = async () => {
            if (!selectedPoi?.studioFloorID || !selectedPoi?.studioPoiId || !floorGuideType || !isMapReady || !isDestinationIconReady) {
                return;
            }

            // Kiểm tra xem POI này đã được xử lý chưa để tránh chạy lại
            const currentPoiKey = `${selectedPoi.studioPoiId}-${floorGuideType}`;
            if (processedPoiRef.current === currentPoiKey) {
                return;
            }

            // Đánh dấu đang xử lý POI này
            processedPoiRef.current = currentPoiKey;

            const poiItem = getPoiById(mapData?.dataPoi?.getPois(), selectedPoi.studioPoiId);
            const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === selectedPoi?.studioFloorID);
            setEndPointFromPoi(poiItem);
            if (selectedPoi?.contentId) {
                setSelectedContentId(selectedPoi.contentId);
            }

            if (selectedPoi && floorGuideType === FloorGuideType.MARKER) {
                const options = calculateGetCurrentPoiOptions(poiItem as IPoi);

                const currentPoiList = await waitForPoisToLoad(mapData, options, 'selectedPoi-MARKER');

                const nearbyPoiItem = getNearestAvailablePoiItem(currentPoiList, allPoiItems);

                const poiContent = handleGetPoiContentById(selectedPoi?.studioPoiId, selectedPoi?.contentId);

                const building = buildings?.find(
                    (b) => (b.storeCode === poiContent?.contents?.cstrCd?.ko && b.buildingCode === poiContent?.contents?.cstrTownCd?.ko) ||  poiContent?.studioBuildingId === b.studioBuildingId
                );
                const buildingName = building?.name?.[language] || building?.name?.['ko'] || '';

                if (Array.isArray(currentPoiList) && currentPoiList?.length) {
                    const POINearByCurrentPoi = nearbyPoiItem;
                    const selectedPoiId = `${poiItem?.id}${POI_ID_DELIMITER}${POINearByCurrentPoi?.id}`;
                    handleSetNearbyState({
                        nearbyPoiList: [{ ...(poiItem as IPoi), nearbyPoi: POINearByCurrentPoi }],
                        selectedPoiId: selectedPoiId,
                        title: poiContent?.name?.[language] || poiContent?.name?.['ko'] || '',
                        floorName: resolveMapFloorName(floor?.name) ?? '',
                        building: buildingName ?? '',
                        type: 'show-location',
                    });
                    handleSetModalState({ nearbyPoiList: true });
                }

                if (!isJamsil) {
                    await handleChangeFloorByName(resolveMapFloorName(floor?.name));
                } else {
                    await handleChangeFloor(floor?.id);
                    if (floor?.name?.[0]?.text) {
                        setSelectedFLoorName(resolveMapFloorName(floor.name));
                    }
                }

                // add marker
                const poiMarker = mapPoiToMarker(poiItem, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
                const markerIds = await addMarker({ marker: poiMarker ? [poiMarker] : [] });

                let markerId: string | undefined;
                if (Array.isArray(markerIds) && markerIds?.length) {
                    markerId = markerIds[0];
                    setMarkerIds({ selectedPoi: markerId });
                }

                // Đợi và verify marker đã được vẽ trên map
                if (markerId && isValidNumber(poiItem?.position?.x) && isValidNumber(poiItem?.position?.y)) {
                    await waitForMarkerReady(currentMap, markerId, { x: poiItem?.position?.x, y: poiItem?.position?.y }, 10, 'MARKER-FloorGuide-Vertical');

                    // Safe moveTo với retry logic
                    await safeMoveToMarker(
                        currentMap,
                        { x: poiItem?.position?.x ?? 0, y: poiItem?.position?.y ?? 0 },
                        poiItem?.floorId,
                        true,
                        3,
                        'MARKER-FloorGuide-Vertical'
                    );
                }
                // reset State
                if (!isJamsil) {
                    resetSelectedPoi();
                }
                resetFloorGuideType();
                processedPoiRef.current = null;
                return;
            }

            // create route
            const destinationPoint = {
                floorId: poiItem?.floorId ?? '',
                floorName: resolveMapFloorName(floor?.name) ?? '',
                position: {
                    x: poiItem?.position?.x ?? 0,
                    y: poiItem?.position?.y ?? 0,
                },
                poiId: poiItem?.id,
            };

            const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, destinationPoint));
            if (!routeDirections || !Object.keys(routeDirections)?.length) {
                processedPoiRef.current = null;
                return;
            }

            clearMarker(markerIds.myLocation ?? '');
            setMarkerIds({ myLocation: undefined });
            setRoutes(routeDirections);
            if (startPoint?.floorName !== destinationPoint.floorName) {
                handleSetModalState({ selectTransportationMode: true });
                handleResetModalState();
                resetSelectedPoi();
                resetFloorGuideType();
                processedPoiRef.current = null;
                return;
            }

            setSelectedRoute(routeDirections?.recommendation);
            await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
            trackFloorGuideNavigation(
                buildNavigationParams({
                    startBuildingId: server?.building?.studioBuildingId ?? '',
                    startPoint,
                    endBuildingId: selectedPoi?.buildingId || currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                    endPoint: destinationPoint,
                    endPoiId: poiItem?.id ?? '',
                })
            );
            handleResetModalState();
            handleSetModalState({ navigationGuide: true });
            handleStart(walkingOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });

            // reset state
            resetSelectedPoi();
            resetFloorGuideType();
            processedPoiRef.current = null;
        };

        run();
    }, [
        currentMap,
        selectedPoi,
        floorGuideType,
        isMapReady,
        isDestinationIconReady,
        destinationIconUrl,
        startPoint,
        mapData?.dataPoi,
        mapData?.dataFloor,
        navigationOptions,
        walkingOptions,
        markerIds.myLocation,
        calculateIconSize,
        handleChangeFloorByName,
        getRouteDirection,
        handleSetModalState,
        handleResetModalState,
        setRoutes,
        resetSelectedPoi,
        resetFloorGuideType,
        addMarker,
        handleStart,
        drawRouteSimulation,
        setSelectedRoute,
        clearMarker,
        setMarkerIds,
        handleSetState,
        setEndPointFromPoi,
        handleSetNearbyState,
        currentMap?.pois?.getCurrentPois,
        handleGetPoiContentById,
        setSelectedContentId,
        handleGetBuildingNameById,
        language,
        selectedFloorName,
        mapData,
        allPoiItems,
    ]);

    useEffect(() => {
        if (contrast) {
            updateObjectGroup(mapData, OBJECT_GROUP);
            updateObjectStyle(currentMap, OBJECT_GROUP);
            setPoi(MAP_CONTRAST_COLOR.POI);
        } else {
            resetObjectStyle(currentMap);
            resetPoi();
        }
    }, [currentMap, mapData, contrast, resetPoi, setPoi]);

    const disableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: false,
                enablePan: false,
            },
            touchOption: {
                enableZoom: false,
                enablePan: false,
            },
        });
    }, [currentMap]);

    const enableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: true,
                enablePan: true,
            },
            touchOption: {
                enableZoom: true,
                enablePan: true,
            },
        });
    }, [currentMap]);

    const isPoiDetailModalOpen = poiDetailModalState.poiDetail;

    const mapContainerStyle = useMemo((): CSSProperties => {
        if (isPoiDetailModalOpen && !zoomClosed) {
            return {
                ...mapBarrierFreeStyle,
                pointerEvents: 'none',
            };
        }

        return mapBarrierFreeStyle;
    }, [isPoiDetailModalOpen, mapBarrierFreeStyle, zoomClosed]);

    const mapInnerStyle = useMemo((): CSSProperties => {
        if (!zoomClosed && isPoiDetailModalOpen) {
            return { pointerEvents: 'none' };
        }

        return { pointerEvents: 'auto' };
    }, [zoomClosed, isPoiDetailModalOpen]);

    useEffect(() => {
        if (!currentMap) {
            return;
        }

        if (zoomClosed) {
            enableMouseEvent();
        } else {
            disableMouseEvent();
        }
    }, [currentMap, zoomClosed, enableMouseEvent, disableMouseEvent]);

    useMagnifyMapScrollThrough(!zoomClosed, isMapReady);

    // Khi POI detail mở trong lúc phóng to, khoá scroll của container zoom để modal không bị kéo lệch khỏi khung nhìn.
    useEffect(() => {
        if (zoomClosed || !isPoiDetailModalOpen) {
            return;
        }

        const zoomScrollContainer = document.querySelector<HTMLElement>(ZOOM_SCROLL_CONTAINER_SELECTOR);
        if (!zoomScrollContainer) {
            return;
        }

        const { overflow, overflowY } = window.getComputedStyle(zoomScrollContainer);
        if (overflow !== 'auto' && overflowY !== 'auto') {
            return;
        }

        const previousOverflow = zoomScrollContainer.style.overflow;
        zoomScrollContainer.style.overflow = 'hidden';

        return () => {
            zoomScrollContainer.style.overflow = previousOverflow;
        };
    }, [zoomClosed, isPoiDetailModalOpen]);

    // Map container đổi kích thước khi scale nghịch hoặc khi ẩn/hiện all-floors guide, cần thông báo để SDK vẽ lại canvas.
    useEffect(() => {
        if (!isMapReady) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [isMapReady, zoomClosed, barrierFreeScale, state.isShowAllFloorGuide]);

    useInteraction(containerRef, () => {
        // Don't reset countdown if TimeExtensionModal is open
        // Other modals should still allow countdown reset on interaction
        const { isModalInStack } = useModalStackStore.getState();
        const isTimeExtensionModalOpen = isModalInStack('time-extension-modal');

        if (!isTimeExtensionModalOpen) {
            resetCountdown();
        }
    });

    return (
        <section className={styles['vertical-view']}>
            <section className={styles['vertical-view__content']}>
                <VerticalMapHeader
                    floorName={selectedFloor?.name}
                    categoryOnFloor={selectedFloor?.detail}
                    isShowAllFloorGuide={state.isShowAllFloorGuide}
                />
                <div className={clsx(styles['vertical-view__map-container'])}>
                    <div
                        ref={containerRef}
                        className={clsx('relative flex-1', {
                            [styles['vertical-view__map-container__show']]: !state.isShowAllFloorGuide,
                            [styles['vertical-view__map-container__hide']]: state.isShowAllFloorGuide,
                        })}
                        style={mapContainerStyle}
                    >
                        <div
                            className={clsx('h-full w-full', {
                                [styles['vertical-view__map-container__magnify-scroll']]: !zoomClosed,
                            })}
                            ref={mapContainerRef}
                            style={mapInnerStyle}
                        />
                    </div>
                    {state.isShowAllFloorGuide && (
                        <AllFloorsGuide
                            onSelectFloor={async (floorName: string, floorId?: string, buildingId?: string) => {
                                handleSetState({ isShowAllFloorGuide: false });
                                await handleStopSimulation();
                                if (!isJamsil) {
                                    await handleChangeFloorByName(floorName);
                                } else {
                                    await handleChangeFloor(floorId);
                                    setCurrentMapBuildingId(buildingId);
                                }
                                speak(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
                            }}
                        />
                    )}
                    <div
                        className={clsx(styles['vertical-view__category-list'], {
                            [styles['vertical-view__category-list--visible']]: state.isShowCategoryList,
                            [styles['vertical-view__category-list--dismissible']]: screenMode === 'fullscreen',
                            [styles['vertical-view__map-container__show']]: !state.isShowAllFloorGuide,
                            [styles['vertical-view__map-container__hide']]: state.isShowAllFloorGuide,
                        })}
                    >
                        <VerticalCategoryList
                            isShowCategoryList={state.isShowCategoryList}
                            onSelectCategory={handleSelectCategory}
                        />
                    </div>
                    <section
                        className={clsx(
                            'fullscreen:translate-x-0 absolute bottom-[calc(120*var(--vh-unit))] right-[calc(24*var(--vh-unit))]',
                            {
                            hidden: state.isShowAllFloorGuide,
                            block: !state.isShowAllFloorGuide,
                            }
                        )}
                    >
                        <ZoomControlBtnGroup
                            tabIndex={6}
                            disabledZoomIn={isDisabledZoomIn}
                            disabledZoomOut={isDisabledZoomOut}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                        />
                    </section>
                    <div data-region-nav={!state.isShowAllFloorGuide && !zoomClosed} data-region-order={3}>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['vertical-view__change-zoom'], styles['vertical-view__change-zoom--up'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={3}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.up')}
                                icon={<ChevronUpIcon />}
                                onClick={moveUp}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['vertical-view__change-zoom'], styles['vertical-view__change-zoom--left'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={3}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.left')}
                                icon={<ChevronLeftIcon />}
                                onClick={moveLeft}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['vertical-view__change-zoom'], styles['vertical-view__change-zoom--down'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={5}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.down')}
                                icon={<ChevronDownIcon />}
                                onClick={moveDown}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['vertical-view__change-zoom'], styles['vertical-view__change-zoom--right'], {
                                ['right-[calc(152*var(--vw-unit))]']: state.isShowCategoryList,
                                ['right-[calc(48*var(--vw-unit))]']: !state.isShowCategoryList,
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={4}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.right')}
                                icon={<ChevronRightIcon />}
                                onClick={moveRight}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                    </div>
                </div>
                <VerticalFloorList
                    floors={sortedFloors}
                    selectedFloorName={selectedFloorName}
                    onSelectFloor={async (floor) => {
                        handleSetModalState({ nearbyPoiList: false });
                        handleResetNearbyState();
                        handleSetState({ isShowAllFloorGuide: false });
                        stopRouteSimulation();
                        clearRouteSimulation();
                        handleSetModalState({ navigationGuide: false });
                        setRoutes(undefined);
                        handleSetState({ navigationStatus: NavigationStatus.DONE });
                        if (!isJamsil) {
                            resetSelectedPoi();
                        }
                        resetFloorGuideType();
                        if (markerIds?.selectedPoi) {
                            clearMarker(markerIds?.selectedPoi);
                        }
                        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
                            clearMarker(markerIds?.poiList);
                        }
                        setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                        await handleSelectStoreFloor(floor);
                    }}
                    isShowAllFloorGuide={state.isShowAllFloorGuide}
                    setIsShowAllFloorGuide={async (isShow: boolean) => {
                        handleSetModalState({ nearbyPoiList: false });
                        handleResetNearbyState();
                        handleSetState({ isShowAllFloorGuide: isShow });
                        handleSetModalState({ navigationGuide: false });
                        await handleStopSimulation();
                    }}
                    onShowMyLocation={async () => await handleShowMyLocation('show-location')}
                    onShowPoiList={() => {
                        handleSetModalState({ poiList: !modalState.poiList });
                        handleSetModalState({ nearbyPoiList: false });
                        handleResetNearbyState();
                    }}
                    currentMapBuildingId={currentMapBuildingId}
                />
                <div
                    className={clsx(styles['vertical-view__navigation-guide'], {
                        [styles['vertical-view__navigation-guide--visible']]: modalState.navigationGuide,
                    })}
                >
                    <NavigationGuide
                        isActive={modalState.navigationGuide}
                        isShowPauseBtn={state.navigationStatus === NavigationStatus.RUNNING || state.navigationStatus === NavigationStatus.IDLE}
                        isShowResumeBtn={state.navigationStatus === NavigationStatus.PAUSED}
                        isShowWatchAgainBtn={state.navigationStatus === NavigationStatus.DONE}
                        directionMaxVisibleRows={screenMode === 'split' ? 3 : undefined}
                        qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, endPoint?.poiId ?? '')}
                        buildingName={currentBuilding?.name?.[i18n.language] || currentBuilding?.name?.['ko'] || ''}
                        directions={generateDisplayDirections(
                            t,
                            (floorId: string) => {
                                const floorName = getFloorById(floorId)?.name?.[0]?.text ?? '';
                                return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                            },
                            selectedRoute?.navigationList,
                            poiItemBySelectedEndPointId?.name?.[language] || poiItemBySelectedEndPointId?.name?.['ko'] || '',
                            (buildingId?: string) => {
                                if (!Array.isArray(buildings) || !buildings?.length) {
                                    return '';
                                }
                                const building = buildings.find((building) => building?.studioBuildingId === buildingId);
                                return building?.name?.[language] || building?.name?.['ko'] || '';
                            }
                        )}
                        startFloorName={server?.floor?.name?.[i18n.language] || server?.floor?.name?.['ko'] || ''}
                        endPoint={generateNavigationGuideEndPoint}
                        onStop={handleStopSimulation}
                        onResume={handleResumeSimulation}
                        onPause={handlePauseSimulation}
                        onWatchAgain={handleWatchAgain}
                    />
                </div>
                {modalState.nearbyPoiList && (
                    <div className={styles['vertical-view__nearby-poi-list-dropdown']}>
                        <NearbyPoiListDropdown
                            type={nearbyState?.type}
                            isActive={modalState.nearbyPoiList}
                            poiList={nearbyState.nearbyPoiList}
                            selectedPoiId={nearbyState.selectedPoiId}
                            onSelectPoi={(poi) => {
                                const selectedPoiId = `${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}`;
                                handleSetNearbyState({ selectedPoiId: selectedPoiId });
                            }}
                            onStartNavigationGuide={async (poi) => {
                                handleSetModalState({ nearbyPoiList: false });
                                const poiListMarkerIds = Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length ? markerIds?.poiList : [];
                                const clearMarkerIds = [markerIds?.selectedPoi ?? '', ...poiListMarkerIds];
                                clearMarker(clearMarkerIds);
                                setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                                await handleStartNavigationGuide(poi as IPoi);
                            }}
                            onClose={handleCloseNearbyPoiList}
                            title={nearbyState?.title ?? ''}
                            floorName={nearbyState?.floorName ?? ''}
                            buildingName={nearbyState?.building ?? ''}
                        />
                    </div>
                )}
            </section>

            {modalState.poiList && (
                <PoiListPopup
                    isOpen={modalState.poiList}
                    onClose={() => {
                        handleSetModalState({ poiList: false });
                    }}
                    currentFloorName={selectedFloorName}
                    mapData={mapData}
                    handlePoiClick={(data) => {
                        handlePoiClick(data);
                        handleSetState({ shouldReopenPoiList: true });
                    }}
                />
            )}

            {modalState.noPoiCategory && (
                <NoCategoryPoiPopup
                    isOpen={modalState.noPoiCategory}
                    onClose={async () => {
                        restoreFocus();
                        handleSetModalState({ noPoiCategory: false });
                    }}
                    category={selectedCategory?.name || selectedCategory?.name}
                    building={handleGetBuildingNameById(state.nearestPoiFloor?.buildingId || currentMapBuildingId || '')}
                    floorName={state.nearestPoiFloor?.name?.[i18n.language] || state.nearestPoiFloor?.name?.['ko'] || ''}
                    onGoToFloor={handleGoToFloor}
                />
            )}

            {poiDetailModalState.poiDetail &&
                poiContentSlide &&
                (isMinimalPoiDetail ? (
                    <MinimalPoiModal
                        isOpen={poiDetailModalState.poiDetail}
                        language={language}
                        poiContent={poiContentSlide}
                        showLocationCheck={false}
                        onClose={() => {
                            handleSetPoiDetailModalState({ poiDetail: false });
                            if (state.shouldReopenPoiList) {
                                handleSetState({ shouldReopenPoiList: false });
                                handleSetModalState({ poiList: true });
                            }
                        }}
                        onLocationCheck={() => undefined}
                        onGuideStart={() => {
                            handleStartGuide();
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetPoiDetailModalState({ poiDetail: false });
                            handleSetModalState({ poiList: false });
                        }}
                    />
                ) : (
                    <FloorGuideVerticalPoiModal
                        isOpen={poiDetailModalState.poiDetail}
                        language={language}
                        poiContent={poiContentSlide}
                        relatedPoiContents={relatedPoiContentList}
                        selectedPoiContentId={selectedPoiContentId}
                        screenMode={screenMode}
                        onSelectPoiContent={(poi) => {
                            setSelectedPoiContent(poi);
                            setSelectedContentId(poi.id);
                            TrackingService.trackPoi(poi.id, undefined, poi);
                        }}
                        onClose={() => {
                            handleSetPoiDetailModalState({ poiDetail: false });
                            if (state.shouldReopenPoiList) {
                                handleSetState({ shouldReopenPoiList: false });
                                handleSetModalState({ poiList: true });
                            }
                        }}
                        onGuideStart={() => {
                            handleStartGuide();
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetPoiDetailModalState({ poiDetail: false });
                            handleSetModalState({ poiList: false });
                        }}
                    />
                ))}

            {modalState.selectTransportationMode && isNonEmptyObject(routes) && (
                <SelectTransportationModeModal
                    isOpen={modalState.selectTransportationMode}
                    onClose={async () => {
                        handleSetModalState({ selectTransportationMode: false });
                        handleSetState({ navigationStatus: NavigationStatus.IDLE });
                        setRoutes(undefined);
                        setSelectedRoute(undefined);
                        resetSelectedPoi();
                        resetFloorGuideType();
                        await handleShowMyLocation();
                    }}
                    onSelect={handleSelectTransportationMode}
                    routes={isNonEmptyObject(routes) ? (Object.keys(routes) as TRANSPORTATION_REQUEST_TYPE[]) : undefined}
                />
            )}

            {loading && <ContentLoading loading />}
        </section>
    );
}
